import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, Pencil, Pin, Plus, Sparkles, Trash2, Info } from 'lucide-react';
import api from '@/lib/api';
import { formatEastern } from '@/lib/format';
import { useAuth } from '@/contexts/AuthContext';
import { useConfirm } from '@/components/ConfirmDialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

/**
 * AI style notes (backend app/style_notes): what this client wants for this product, from their Style Profile
 * and their feedback on past projects. Part of the project's one Notes section (components/NotesSection.js).
 * For the assigned editor they REPLACE the Style Profile (Ryan, 2026-09-25), so every profile answer is here,
 * marked "Style Profile"; with no notes, or none that carry the whole profile, the Style Profile itself shows in
 * their place. "See what the client wrote in their Style Profile" opens the answers as written. Admin and staff
 * see the same notes and can pin, edit or delete a line, and add their own note (what the client told them by
 * phone, email or text) for one product type or all of them; it is shown as added by the team, with the date.
 * A line the client said applies to all their work shows under All products with their words.
 *
 * `renderProfile(profile)` renders the drawer's own Style Profile view; `fallbackProfile` is used when the notes
 * could not be read at all. `url` reads them another way (the Redemption Cut page reads them through the
 * redemption, since the project is not its editor's).
 */
const ALL = 'All products';
const TYPES = [ALL, 'Highlights', 'Doc edits', 'Social media', 'Photos'];

// A capturing split puts every link at an odd index.
function Linked({ text }) {
  return String(text).split(/(https?:\/\/[^\s]+)/).map((part, i) => (i % 2 === 1)
    ? <a key={i} href={part} target="_blank" rel="noopener noreferrer"
        className="text-oxidized-teal hover:text-oxidized-teal/80 underline underline-offset-2 break-all">{part}</a>
    : <span key={i}>{part}</span>);
}

function slug(s) {
  return String(s).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

function Line({ line, team, docId, editing, setEditing, onChanged }) {
  const confirm = useConfirm();
  const [busy, setBusy] = useState(false);
  const isEditing = editing && editing.lineId === line.id;
  const act = async (fn, done) => {
    setBusy(true);
    try { await fn(); toast.success(done); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'That did not save. Please try again.'); }
    finally { setBusy(false); }
  };
  const pin = () => act(() => api.post(`/style-notes/${docId}/lines/${line.id}/pin`, { pinned: !line.pinned }),
    line.pinned ? 'Unpinned.' : 'Pinned. It stays when the notes update.');
  const save = () => act(async () => {
    await api.put(`/style-notes/${docId}/lines/${line.id}`, { text: editing.text });
    setEditing(null);
  }, 'Saved. Your wording stays when the notes update.');
  const remove = async () => {
    const ok = await confirm({
      title: 'Remove this line?',
      message: line.source === 'team' ? 'It is gone for good.' : 'It stays gone unless new feedback from the client brings it up again.',
      confirmLabel: 'Remove', cancelLabel: 'Keep', destructive: true });
    if (ok) act(() => api.delete(`/style-notes/${docId}/lines/${line.id}`), 'Removed.');
  };
  return (
    <li className="flex items-start gap-2" data-testid="style-note-line" data-source={line.source} data-seen-once={line.seen_once ? '1' : '0'}>
      <span className="text-ug-gold/60 shrink-0 text-[12px] leading-relaxed">&bull;</span>
      {isEditing ? (
        <div className="flex-1 min-w-0 space-y-2">
          <textarea value={editing.text} onChange={e => setEditing({ ...editing, text: e.target.value })} rows={2} autoFocus
            data-testid="style-note-edit-input" maxLength={600}
            className="w-full bg-ug-surface border border-ug-border text-zinc-100 rounded-md p-2 text-[12px] focus:border-gold-leaf/40 outline-none resize-y" />
          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="ghost" onClick={() => setEditing(null)} data-testid="style-note-edit-cancel"
              className="tap-auto text-slate-500 h-8 text-[10px] px-2">Cancel</Button>
            <Button size="sm" onClick={save} disabled={busy || !editing.text.trim()} data-testid="style-note-edit-save"
              className="tap-auto bg-gold-leaf text-obsidian hover:bg-gold-leaf/90 h-8 text-[10px] px-3">Save</Button>
          </div>
        </div>
      ) : (
        <span className="flex-1 min-w-0 text-[12px] text-zinc-100/85 leading-relaxed break-words">
          <Linked text={line.text} />
          {(line.files || []).map(f => (
            <span key={f.file_id}> <a href={`${process.env.REACT_APP_BACKEND_URL}/api/storage/serve/${f.file_id}`}
              className="tap-auto text-oxidized-teal hover:text-oxidized-teal/80 underline underline-offset-2 break-all"
              data-testid="style-note-file">{f.name}</a></span>
          ))}
          {line.source === 'profile' && <span className="text-[10px] text-slate-500 whitespace-nowrap"> &middot;&nbsp;Style Profile</span>}
          {line.source === 'team' && (
            <span className="text-[10px] text-slate-500 whitespace-nowrap" data-testid="style-note-team-tag">
              {' '}&middot;&nbsp;Added by our team{line.added_at ? `, ${formatEastern(line.added_at, { dateOnly: true })}` : ''}
            </span>
          )}
          {line.seen_once && <span className="text-[10px] text-slate-500 whitespace-nowrap"> (seen once)</span>}
          {team && line.edited && (
            <span className="text-[10px] text-slate-500 whitespace-nowrap"
              title={line.edited_by?.name ? `Edited by ${line.edited_by.name}` : undefined}> &middot;&nbsp;edited</span>
          )}
          {line.said_all && (
            <span className="block text-[10px] text-slate-500 leading-relaxed mt-0.5" data-testid="style-note-said-all">
              Client said this applies to everything: <span className="italic text-zinc-100/70">"{line.said_all}"</span>
            </span>
          )}
        </span>
      )}
      {team && !isEditing && (
        <span className="flex items-center gap-1.5 shrink-0 mt-[3px]">
          {line.source !== 'team' && (   // an added note is pinned for good: nothing rewrites it
            <button type="button" onClick={pin} disabled={busy} data-testid={`style-note-pin-${line.id}`}
              title={line.pinned ? 'Unpin' : 'Pin: keep this line when the notes update'} aria-pressed={!!line.pinned}
              className={`tap-auto transition-colors ${line.pinned ? 'text-ug-gold' : 'text-slate-500 hover:text-ug-gold'}`}>
              <Pin className="w-3 h-3" fill={line.pinned ? 'currentColor' : 'none'} />
            </button>
          )}
          <button type="button" onClick={() => setEditing({ lineId: line.id, text: line.text })} disabled={busy}
            data-testid={`style-note-edit-${line.id}`} title="Edit this line"
            className="tap-auto text-slate-500 hover:text-ug-gold transition-colors"><Pencil className="w-3 h-3" /></button>
          <button type="button" onClick={remove} disabled={busy} data-testid={`style-note-delete-${line.id}`} title="Remove this line"
            className="tap-auto text-slate-500 hover:text-ug-status-red transition-colors"><Trash2 className="w-3 h-3" /></button>
        </span>
      )}
    </li>
  );
}

// "Add a note" (Ryan, 2026-09-25): admin and staff write what the client told them, for one product type or all.
function AddNote({ projectId, productType, onAdded }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [type, setType] = useState(productType || ALL);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/projects/${projectId}/style-notes/lines`, { text: text.trim(), product_type: type });
      toast.success(`Added to the notes for ${type === ALL ? 'all products' : type}. It stays when the notes update.`);
      setOpen(false); setText(''); onAdded();
    } catch (e) { toast.error(e?.response?.data?.detail || 'That did not save. Please try again.'); }
    finally { setBusy(false); }
  };
  if (!open) {
    return (
      <button type="button" onClick={() => { setType(productType || ALL); setOpen(true); }} data-testid="style-notes-add"
        className="tap-auto inline-flex items-center gap-1 text-[10px] text-oxidized-teal hover:text-oxidized-teal/80">
        <Plus className="w-3 h-3" /> Add a note
      </button>
    );
  }
  return (
    <div className="space-y-2" data-testid="style-notes-add-form">
      <textarea value={text} onChange={e => setText(e.target.value)} rows={2} autoFocus maxLength={600}
        data-testid="style-notes-add-input" placeholder="What should editors know? For example: no slow motion in the recessional."
        className="w-full bg-ug-surface border border-ug-border text-zinc-100 rounded-md p-2 text-[12px] focus:border-gold-leaf/40 outline-none resize-y placeholder:text-white/40" />
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] text-slate-500">For</span>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger data-testid="style-notes-add-type" className="tap-auto w-[150px] bg-ug-surface border-ug-border text-zinc-100 h-8 text-[11px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-ug-raised border-ug-border-strong">
            {TYPES.map(t => <SelectItem key={t} value={t} className="text-zinc-100 text-[11px]">{t}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="flex-1" />
        <Button size="sm" variant="ghost" onClick={() => { setOpen(false); setText(''); }} data-testid="style-notes-add-cancel"
          className="tap-auto text-slate-500 h-8 text-[10px] px-2">Cancel</Button>
        <Button size="sm" onClick={save} disabled={busy || !text.trim()} data-testid="style-notes-add-save"
          className="tap-auto bg-gold-leaf text-obsidian hover:bg-gold-leaf/90 h-8 text-[10px] px-3">Save</Button>
      </div>
    </div>
  );
}

const label = 'font-sans text-[9px] font-semibold uppercase tracking-wider';

export function StyleNotes({ project, fallbackProfile, renderProfile, url }) {
  const { user } = useAuth();
  const team = user?.role === 'admin' || user?.role === 'staff';
  const notesUrl = url || `/projects/${project.id}/style-notes`;
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(notesUrl);
      setData(r.data);
    } catch {
      setData({ show: 'profile' });   // never leave the editor with neither
    }
  }, [notesUrl]);

  useEffect(() => { setData(null); setOpen(false); setEditing(null); load(); }, [load]);

  if (!data) return <p className="text-[11px] text-slate-600" data-testid="style-notes-loading">Loading style notes…</p>;
  const profile = data.profile || fallbackProfile;
  const lineProps = { team, editing, setEditing, onChanged: load };
  const add = team && data.product_type && <AddNote projectId={project.id} productType={data.product_type} onAdded={load} />;
  // The gaps around these blocks sit on the blocks themselves (mb-3, mt-3): the dev build's visual-edits plugin wraps
  // JSX expressions in an inline span, which a parent's space-y cannot reach (the same note as in MustHaves.js).
  if (data.show !== 'notes') {
    const added = data.team_lines || [];
    return (
      <div data-testid="style-notes-fallback">
        {added.length > 0 && (
          <ul className="space-y-1.5 mb-3" data-testid="style-notes-team-lines">
            {added.map(l => <Line key={l.id} line={l} docId={l.doc_id} {...lineProps} />)}
          </ul>
        )}
        {add && <div className="mb-3">{add}</div>}
        {renderProfile(profile)}
      </div>
    );
  }

  const lines = data.lines || [];
  const added = lines.filter(l => l.source === 'team');
  return (
    <>
      <div className="space-y-3" data-testid="style-notes">
        <h4 className="font-sans text-[11px] font-medium uppercase tracking-wider text-ug-gold flex items-center gap-2">
          <Sparkles className="w-4 h-4" /> Style notes
          <span className="text-slate-500 text-[10px] normal-case tracking-normal font-normal">{data.product_type}</span>
        </h4>
        <p className="text-[11px] text-slate-500 leading-relaxed" data-testid="style-notes-intro">
          What {data.brand} wants, from their Style Profile and their notes on past projects.
        </p>
        {data.all_products?.length > 0 && (
          <div data-testid="style-notes-all-products">
            <div className={`${label} text-slate-500 mb-1.5`}>All products</div>
            <ul className="space-y-1.5">
              {data.all_products.map(l => <Line key={l.id} line={l} docId={data.all_products_doc_id} {...lineProps} />)}
            </ul>
          </div>
        )}
        <div className="space-y-2.5" data-testid="style-notes-type">
          <div className={`${label} text-slate-500 ${data.all_products?.length ? 'pt-2 border-t border-ug-border' : ''}`}>{data.product_type}</div>
          {added.length > 0 && (
            <ul className="space-y-1.5 mt-2.5" data-testid="style-notes-team-lines">
              {added.map(l => <Line key={l.id} line={l} docId={data.doc_id} {...lineProps} />)}
            </ul>
          )}
          {(data.headings || []).map(h => {
            const mine = lines.filter(l => l.heading === h && l.source !== 'team');
            if (!mine.length) return null;
            return (
              <div key={h} data-testid={`style-notes-heading-${slug(h)}`}>
                <div className={`${label} text-ug-gold/60 mb-1`}>{h}</div>
                <ul className="space-y-1.5">
                  {mine.map(l => <Line key={l.id} line={l} docId={data.doc_id} {...lineProps} />)}
                </ul>
              </div>
            );
          })}
          {(data.flags || []).map(f => (
            <div key={f.id} className="flex items-start gap-2 text-[12px] text-amber-300 leading-relaxed" data-testid="style-note-flag">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-[3px]" /><span>{f.text}</span>
            </div>
          ))}
          {/* Conflicts the client never said hold going forward: the editor follows the Style Profile and never sees
              these; the team is shown them to raise with the client (app/style_notes/writer.py check). */}
          {(data.team_flags || []).map(f => (
            <div key={f.id} className="flex items-start gap-2 text-[12px] text-slate-400 leading-relaxed" data-testid="style-note-team-flag">
              <Info className="w-3.5 h-3.5 shrink-0 mt-[3px]" />
              <span><span className="font-sans text-[9px] font-semibold uppercase tracking-wider text-slate-500 mr-1.5">For the team, editors don&apos;t see this</span>{f.text}</span>
            </div>
          ))}
          {/* Doc edits: general Style Profile answers these notes left out because none of them applies to a doc edit
              (app/style_notes/writer.py, OPTIONAL answers). The team sees what and why; the editor never does. */}
          {(data.left_out || []).map((x, i) => (
            <div key={`lo-${i}`} className="flex items-start gap-2 text-[12px] text-slate-400 leading-relaxed" data-testid="style-note-left-out">
              <Info className="w-3.5 h-3.5 shrink-0 mt-[3px]" />
              <span><span className="font-sans text-[9px] font-semibold uppercase tracking-wider text-slate-500 mr-1.5">Left out for doc edits, editors don&apos;t see this</span>
                {x.label}{x.why ? `: ${x.why}` : ''}</span>
            </div>
          ))}
        </div>
        {add && <div className="mt-3">{add}</div>}
      </div>
      <div className="pt-3 border-t border-ug-border">
        <button type="button" onClick={() => setOpen(o => !o)} data-testid="style-notes-see-original" aria-expanded={open}
          className="tap-auto text-[10px] text-oxidized-teal hover:text-oxidized-teal/80 underline underline-offset-2 text-left">
          {open ? 'Hide what the client wrote in their Style Profile' : 'See what the client wrote in their Style Profile'}
        </button>
        {open && <div className="mt-3" data-testid="style-notes-original">{renderProfile(profile)}</div>}
      </div>
    </>
  );
}
